/**
 * {PLAYER}-style placeholders, painted wherever text is shown.
 *
 * These are the one part of a prompt a translator must carry across untouched:
 * the app substitutes a name into them at runtime, so a dropped or invented
 * token ships a broken card. Marking them makes them read as machinery rather
 * than as words, in the box being typed into as much as in the English.
 */
const TOKEN = /\{[A-Z_]+\}/g;

export const tokensOf = (text: string): string[] => text.match(TOKEN) ?? [];

const escapeHtml = (s: string) =>
  s.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));

/**
 * Token spans for text going into a contenteditable.
 *
 * Escaped first: this is a reviewer's own typing, and a stray `<` would
 * otherwise disappear into the markup.
 */
export function paintHtml(text: string, source: string): string {
  const want = tokensOf(source);
  const seen = new Map<string, number>();
  return escapeHtml(text).replace(TOKEN, (t) => {
    const nth = (seen.get(t) ?? 0) + 1;
    seen.set(t, nth);
    const allowed = want.filter((w) => w === t).length;
    return `<span class="tok${nth > allowed ? ' bad' : ''}">${t}</span>`;
  });
}

/** Tokens the English has that this text has lost. */
export function missingTokens(text: string, source: string): string[] {
  const have = tokensOf(text);
  const want = tokensOf(source);
  return [...new Set(want)].filter(
    (t) => have.filter((h) => h === t).length < want.filter((w) => w === t).length);
}

/** Where the caret sits, counted in characters rather than in nodes — the
 *  nodes are about to be replaced. */
function caretOffset(el: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.endContainer)) return null;
  const upto = range.cloneRange();
  upto.selectNodeContents(el);
  upto.setEnd(range.endContainer, range.endOffset);
  return upto.toString().length;
}

function setCaret(el: HTMLElement, offset: number) {
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let seen = 0;
  let node = walk.nextNode();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (seen + len >= offset) {
      range.setStart(node, offset - seen);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    seen += len;
    node = walk.nextNode();
  }
  // Past the end — an empty box, or the text shrank under us.
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

/**
 * Re-mark an editable box in place, keeping the caret where the typist left it.
 *
 * Rewriting innerHTML normally throws the caret to the start, which is why
 * highlighting inside a contenteditable is usually done with a mirrored layer
 * underneath. These strings are one sentence long, so measuring the caret in
 * characters and putting it back is both simpler and immune to the two layers
 * wrapping differently.
 */
export function repaint(el: HTMLElement, source: string) {
  const text = el.textContent ?? '';
  const html = paintHtml(text, source);
  if (el.innerHTML === html) return;
  const offset = caretOffset(el);
  el.innerHTML = html;
  if (offset !== null) setCaret(el, offset);
}
